//---------------------------------------------------------------------------------------------------
//  ?                                  tradeMarkerBuilder
//  @author         :  DeeTrade Team
//  @createdOn      :  03/27/2026
//  @description    :  Shared utility to build trade execution markers from signal node settings.
//                     Used by SmartChart (live/paper and backtest) for unified marker rendering.
//---------------------------------------------------------------------------------------------------

import EXIT_REASON_REGISTRY from './exitReasonRegistry.json';

//--> Exit-reason registry: the ONE frontend reason -> {action, label, shape} authority
//*   (exitReasonRegistry.json). Every exit set / label / shape below is DERIVED from it.
export { EXIT_REASON_REGISTRY };

//--> Exit marker colour is the realised outcome ONLY (profit / loss / neutral), applied in
//*   buildTradeMarker after settings resolution; category is carried by shape + label.
export const EXIT_OUTCOME_COLORS = {
    profit: '#00C292',
    loss: '#E46A76',
    neutral: '#94A3B8',
};

//--> Generic side-aware exit actions (no registry reason): rendered with the generic "EXIT" label.
const GENERIC_EXIT_ACTIONS = ['exit_long', 'exit_short'];

const registryRows = Object.values(EXIT_REASON_REGISTRY);
//--> Rows with `sideDefault` have no marker action of their own: they resolve to the born-as
//*   exit_long / exit_short (the graph-node identity findSignalNode matches) or the side default.
const actionRows = registryRows.filter((r) => !r.sideDefault);

//--> Own-property lookup only (operator free text such as "constructor" must not hit the prototype).
const rowFor = (key) => (key && Object.prototype.hasOwnProperty.call(EXIT_REASON_REGISTRY, key)
    ? EXIT_REASON_REGISTRY[key] : null);

//--> Every exit action the registry can produce, plus the two generic side-aware ones.
export const EXIT_ACTION_SET = new Set([...actionRows.map((r) => r.action), ...GENERIC_EXIT_ACTIONS]);
export const isExitAction = (action) => EXIT_ACTION_SET.has(action);

//--> action -> trade-history wording (same label EXIT_REASON_MAP shows); generic exit_long /
//*   exit_short are absent (they take the REASON's label, else the generic "EXIT").
const EXIT_ACTION_LABELS = {};
actionRows.forEach((r) => {
    if (!GENERIC_EXIT_ACTIONS.includes(r.action) && !(r.action in EXIT_ACTION_LABELS)) EXIT_ACTION_LABELS[r.action] = r.label;
});

//--> Marker text for an exit: the REASON's registry label when the reason is known (and either
//*   specific, or generic with no labelled action), then the action's label, then "EXIT".
const exitLabel = (action, exitReason) => {
    const row = rowFor(String(exitReason || '').toLowerCase());
    if (row && (!row.genericFallback || !EXIT_ACTION_LABELS[action])) return row.label;
    return EXIT_ACTION_LABELS[action] || 'EXIT';
};

//--> Default trade marker settings per signal action type. Exit shapes come from the registry;
//*   exit colours are NOT category colours -- they are the neutral placeholder, because
//*   buildTradeMarker overrides every exit colour with the outcome colour.
const exitDefault = (shape, position, autoFlipPosition) => ({
    enabled: true, shape, color: EXIT_OUTCOME_COLORS.neutral, position, autoFlipPosition, size: 2,
    label: { enabled: true, mode: 'action_price' },
});
const registryExitDefaults = {};
actionRows.forEach((r) => {
    if (!registryExitDefaults[r.action]) registryExitDefaults[r.action] = exitDefault(r.shape, 'aboveBar', true);
});

export const TRADE_MARKER_DEFAULTS = {
    enter_long:    { enabled: true, shape: 'arrowUp',   color: '#10B981', position: 'belowBar', autoFlipPosition: false, size: 2, label: { enabled: true, mode: 'action_price' } },
    enter_short:   { enabled: true, shape: 'arrowDown', color: '#EF4444', position: 'aboveBar', autoFlipPosition: false, size: 2, label: { enabled: true, mode: 'action_price' } },
    ...registryExitDefaults,
    exit_long:     exitDefault('square', 'aboveBar', false),
    exit_short:    exitDefault('square', 'belowBar', false),
    //--> 50-5: SIGNAL marker -- the entry order's REQUESTED bar/price (working order, or the
    //*   signal bar when the fill landed on a later bar). Grey circle; 'circle' is a valid shape.
    signal:        { enabled: true, shape: 'circle',    color: '#9CA3AF', position: 'belowBar', autoFlipPosition: false, size: 1, label: { enabled: true, mode: 'action_price' } },
};

//--> Outcome (profit | loss | neutral) from a realised P&L number (backtest trade.pnl).
//*   null / non-finite / zero are neutral.
export const pnlToOutcome = (value) => {
    if (value == null || value === '') return 'neutral';
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return 'neutral';
    return n > 0 ? 'profit' : 'loss';
};

//--> Outcome from the entry/exit fill prices of the position direction (LONG/BUY or SHORT/SELL).
//*   Object form only. Unknown side / null / non-finite price / break-even are neutral.
export const pricesToOutcome = ({ side, entryPrice, exitPrice } = {}) => {
    const dir = ['LONG', 'BUY'].includes(String(side || '').toUpperCase()) ? 1
        : ['SHORT', 'SELL'].includes(String(side || '').toUpperCase()) ? -1 : 0;
    if (!dir || entryPrice == null || exitPrice == null || entryPrice === '' || exitPrice === '') return 'neutral';
    const entry = Number(entryPrice);
    const exit = Number(exitPrice);
    if (!Number.isFinite(entry) || !Number.isFinite(exit)) return 'neutral';
    return pnlToOutcome((exit - entry) * dir);
};

/**
 * Coerce a timestamp to UTC seconds. Naive ISO strings (no Z/+ suffix) are
 * interpreted as UTC — matches SmartChart's moment.utc() parsing so markers
 * align with candles.
 */
const toUtcSec = (s) => {
    if (!s) return NaN;
    const str = typeof s === 'string' && !s.endsWith('Z') && !s.includes('+') ? `${s}Z` : s;
    return Math.floor(new Date(str).getTime() / 1000);
};

//--> Registry row -> marker action. flip_exit carries no direction (uses trade.side); sideDefault
//*   rows keep the born-as exit_long / exit_short node action, else the side default.
const rowToAction = (row, side, direct) => {
    if (row.sideAware) return side === 'SHORT' ? 'flip_short' : 'flip_long';
    if (row.sideDefault) {
        return GENERIC_EXIT_ACTIONS.includes(direct) ? direct : (side === 'SHORT' ? 'exit_short' : 'exit_long');
    }
    return row.action;
};

/**
 * Resolve canonical exit action from persisted trade payload.
 * Supports both camelCase and snake_case fields for backward compatibility.
 * Precedence: a SPECIFIC exit_reason wins; the born-as exit_action / leg_type is consulted only
 * when the reason is missing or a generic registry row (rule_exit / signal). An unknown
 * non-empty reason is the side-aware generic exit -- it never borrows SL / TP.
 */
export const resolveTradeExitAction = (trade = {}) => {
    const direct = String(trade.exitAction || trade.exit_action || '').toLowerCase();
    const reason = String(trade.exitReason || trade.exit_reason || trade.reason || '').toLowerCase();
    const side = String(trade.side || '').toUpperCase();
    const genericAction = side === 'SHORT' ? 'exit_short' : 'exit_long';

    const reasonRow = rowFor(reason);
    if (reasonRow && !reasonRow.genericFallback) return rowToAction(reasonRow, side, direct);
    if (reason && !reasonRow) return genericAction;

    //--> Reason missing or generic: fall back to the born-as action / leg_type.
    const directRow = rowFor(direct);
    if (directRow && !directRow.genericFallback) return rowToAction(directRow, side, direct);
    if (reasonRow) return rowToAction(reasonRow, side, direct);
    if (directRow) return rowToAction(directRow, side, direct);
    return genericAction;
};

/**
 * Build label text for a trade marker based on label settings.
 * @param {{ action: string, side: string, price: number }} trade
 * @param {{ enabled: boolean, mode: 'off'|'price'|'action_price' }} labelSettings
 * @returns {string}
 */
export const buildLabelText = ({ action, side, price, exitReason }, labelSettings) => {
    if (!labelSettings?.enabled) return '';

    const formattedPrice = Number(price).toFixed(2);

    switch (labelSettings.mode) {
        case 'action_price': {
            //! Use side for entry actions, the registry (trade-history) wording for exits.
            const isEntry = action === 'enter_long' || action === 'enter_short';
            const actionLabel = isEntry
                ? side.toUpperCase()
                : action === 'signal' ? 'SIGNAL'
                : exitLabel(action, exitReason);
            return `${actionLabel} @ ${formattedPrice}`;
        }
        case 'price':
            return `@ ${formattedPrice}`;
        case 'off':
        default:
            return '';
    }
};

/**
 * Resolve trade marker settings from a signal node, with stage override merging.
 * @param {object} signalNode - ReactFlow node object or null if no match
 * @param {string} action - Canonical action string (required when signalNode is null)
 * @param {number} stage - 1-indexed stage number
 * @returns {object} Resolved trade marker settings
 */
export const resolveTradeMarkerSettings = (signalNode, action, stage) => {
    //-->  Use action from signalNode if present; otherwise action must be passed explicitly.
    const resolvedAction = signalNode?.data?.action || action;
    const nodeSettings = signalNode?.data?.visual_settings?.trade_markers;
    const defaults = TRADE_MARKER_DEFAULTS[resolvedAction] || TRADE_MARKER_DEFAULTS.enter_long;

    //# Merge: defaults < node base settings < stage overrides
    const base = nodeSettings
        ? { ...defaults, ...nodeSettings, label: { ...defaults.label, ...(nodeSettings.label || {}) } }
        : { ...defaults };

    //--> Apply stage override if present
    const stageOverride = nodeSettings?.stageOverrides?.[stage];
    if (stageOverride) {
        return { ...base, ...stageOverride, label: { ...base.label, ...(stageOverride.label || {}) } };
    }

    return base;
};

/**
 * Auto-flip position based on trade direction.
 * Long exits → aboveBar, Short exits → belowBar.
 */
const flipPosition = (side) => {
    return side?.toUpperCase() === 'SHORT' ? 'belowBar' : 'aboveBar';
};

/**
 * Build a single trade marker object for lightweight-charts.
 * Returns null if trade markers are disabled on the node.
 *
 * @param {{ time: number, price: number, action: string, side: string, stage: number, exitReason?: string, outcome?: 'profit'|'loss'|'neutral' }} tradeEvent
 * @param {object} signalNode - ReactFlow node with visual_settings.trade_markers
 * @returns {object|null} Marker object for createSeriesMarkers(), or null if disabled
 */
export const buildTradeMarker = (tradeEvent, signalNode) => {
    const settings = resolveTradeMarkerSettings(signalNode, tradeEvent.action, tradeEvent.stage);

    if (!settings.enabled) return null;

    //--> Resolve position (auto-flip for exit nodes)
    let position = settings.position;
    if (settings.autoFlipPosition) {
        position = flipPosition(tradeEvent.side);
    }

    //! Exit colour = realised outcome ONLY, decided here once AFTER settings resolution so a
    //!  node / stage / saved colour can never win. Entry and signal colours stay configurable.
    const color = isExitAction(tradeEvent.action)
        ? EXIT_OUTCOME_COLORS[tradeEvent.outcome] || EXIT_OUTCOME_COLORS.neutral
        : settings.color;

    return {
        time: tradeEvent.time,
        shape: settings.shape,
        color,
        position,
        size: settings.size,
        text: buildLabelText(tradeEvent, settings.label),
    };
};

/**
 * Build an ordered list of trade markers from a persisted trades payload.
 *
 * Canonical behavior (extracted verbatim from StrategyBuilder.jsx): naive ISO
 * timestamps (no 'Z' / no offset) are interpreted as UTC by appending 'Z' before
 * parsing. This keeps markers aligned with SmartChart's UTC-parsed candles.
 *
 * @param {Array} trades - Persisted trade objects (camel or snake case fields)
 * @param {object} [opts]
 * @param {Array}  [opts.nodes]     ReactFlow nodes; signal/position_trigger nodes
 *                                  keyed by `${action}_${stage}` feed marker styling.
 * @param {string} [opts.timeframe] Accepted for API symmetry with buildMarkersFromOrders
 *                                  (Chunk 3b); currently unused.
 * @param {string} [options.tzMode='utc'] Accepted for API symmetry with Chunk 3b's
 *                                  buildMarkersFromOrders; currently unused. The
 *                                  implementation always treats naive ISO as UTC.
 * @returns {Array} Marker objects consumable by lightweight-charts createSeriesMarkers().
 */

/**
 * Canonical timestamp → Unix seconds coercion.
 * Handles: number (seconds or ms), ISO string (naive → UTC, Z/+ suffix preserved),
 *          SQL-style "YYYY-MM-DD HH:MM:SS" (space replaced with T).
 * Returns null for null/undefined/invalid input. Does NOT fall back to Date.now().
 * Callers that need a now-fallback wrap: `toUnixSec(x) ?? Math.floor(Date.now()/1000)`.
 */
export function toUnixSec(v) {
    if (v == null) return null;
    if (typeof v === 'number') return v > 1e12 ? Math.floor(v / 1000) : Math.floor(v);
    if (typeof v === 'string') {
        const normalized = v.replace(' ', 'T');
        const withTz = (normalized.endsWith('Z') || normalized.includes('+')) ? normalized : `${normalized}Z`;
        const ms = new Date(withTz).getTime();
        return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
    }
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

/**
 * Snap a timestamp down to the start of the bar at the given timeframe.
 * Intraday units (m, h) floor in UTC — boundaries align in UTC.
 * Daily (d) floors in IST because NSE daily bars anchor to market open
 * 09:15 IST (= 03:45 UTC = 13500 seconds past UTC midnight).
 * Unknown units (e.g. weekly 'w') pass through unchanged — live UI does
 * not render weekly today; extend when adding weekly support.
 */
export function snapToBar(tsInput, timeframe, anchor) {
    const sec = toUnixSec(tsInput);
    if (sec == null) return null;
    const m = String(timeframe || '1m').match(/(\d+)([mhd])/i);
    if (!m) return sec;
    const n = parseInt(m[1], 10);
    const unit = m[2].toLowerCase();
    if (unit === 'd') {
        //--> day frames snap on the IST-open day grid regardless of `anchor` (50-5 F-4):
        //    that grid is already exchange-aligned, so a fill instant and its order's
        //    bar_start land on the same day bar.
        const IST_OPEN_OFFSET_SEC = 3 * 3600 + 45 * 60;
        const daySec = 86400 * n;
        return Math.floor((sec - IST_OPEN_OFFSET_SEC) / daySec) * daySec + IST_OPEN_OFFSET_SEC;
    }
    const step = unit === 'h' ? n * 3600 : n * 60;
    //--> 50-5: optional bar-aligned `anchor` (e.g. the order's bar_start). When it parses, snap on the
    //*   anchor's grid (handles bar grids offset from UTC multiples) and never earlier than the anchor.
    const a = anchor != null ? toUnixSec(anchor) : null;
    if (a != null) return Math.max(a, a + Math.floor((sec - a) / step) * step);
    return Math.floor(sec / step) * step;
}

/**
 * Build chart markers from live FILLED OMS orders, snapped to the active
 * bar boundary so they align with the candle series.
 */
// eslint-disable-next-line no-unused-vars
export function buildMarkersFromOrders(orders, { timeframe = '1m', resolver = null } = {}) {
    if (!Array.isArray(orders)) return [];
    return orders
        .filter((o) => o?.status === 'FILLED')
        .map((o) => {
            const rawTime = o.timestamp || o.updated_at || o.created_at;
            const time = snapToBar(rawTime, timeframe);
            if (time == null) return null;
            return {
                time,
                price: Number(o.price),
                action: o.action || 'ENTRY',
                side: o.side || 'LONG',
                stage: o.stage || null,
            };
        })
        .filter(Boolean);
}

// eslint-disable-next-line no-unused-vars
export function buildMarkersFromTrades(trades, { nodes, timeframe, tzMode = 'utc' } = {}) {
    if (!Array.isArray(trades) || trades.length === 0) return [];

    //--> Build a lookup: (action, stage) → signal node for marker settings
    const nodesByActionStage = {};
    (nodes || []).forEach((n) => {
        if (n.type === 'signal' || n.type === 'position_trigger') {
            const key = `${n.data?.action}_${n.data?.stage || 1}`;
            nodesByActionStage[key] = n;
        }
    });

    const markers = [];
    trades.forEach((trade) => {
        //--> Anchor precedence mirrors TradeLevelOverlay.toBandSegment so the
        //*   entry marker and the bracket band render on the SAME candle.
        //*   fill_bar_start (live post-fill) → entryTime/entry_time (historic
        //*   carries fill bar already; live pre-fill falls back to signal bar
        //*   which is the expected transient placement until the fill arrives
        //*   and the marker "jumps" to the fill candle).
        const entryTime = trade.fill_bar_start ?? trade.entryTime ?? trade.entry_time;
        const exitTime = trade.exitTime ?? trade.exit_time;
        const entryPrice = Number(trade.entryPrice ?? trade.entry_price ?? 0);
        const exitPrice = Number(trade.exitPrice ?? trade.exit_price ?? 0);
        const side = String(trade.side || '').toUpperCase();
        const stage = trade.stage || 1;
        const entryTs = toUtcSec(entryTime);

        if (!entryTs || Number.isNaN(entryTs)) return;

        //--> Entry marker
        const entryAction = trade.entryAction || (side === 'LONG' || side === 'BUY' ? 'enter_long' : 'enter_short');
        const entryNode = nodesByActionStage[`${entryAction}_${stage}`];
        const entryMarker = buildTradeMarker(
            { time: entryTs, price: entryPrice, action: entryAction, side, stage },
            entryNode || { data: { action: entryAction, visual_settings: {} } },
        );
        if (entryMarker) markers.push(entryMarker);

        //--> Exit marker (if trade is closed)
        if (exitTime) {
            const exitTs = toUtcSec(exitTime);
            if (!exitTs || Number.isNaN(exitTs)) return;

            const exitAction = resolveTradeExitAction(trade);
            const exitNode = nodesByActionStage[`${exitAction}_${stage}`];
            const exitMarker = buildTradeMarker(
                { time: exitTs, price: exitPrice, action: exitAction, side, stage, exitReason: trade.exitReason ?? trade.exit_reason ?? trade.reason, outcome: pnlToOutcome(trade.pnl) },
                exitNode || { data: { action: exitAction, visual_settings: {} } },
            );
            if (exitMarker) markers.push(exitMarker);
        }
    });

    return markers;
}
